import { Typography } from "@mui/material";
import logo from "../img/image-Photoroom (1).png";
import image from "./img/image-Photoroom (2).png";
import { useTranslation } from "react-i18next";

export const PreventiveBill = ({ data, reporteRef }) => {
  const { t } = useTranslation();
  // Extraer partes de la fecha
  const date = new Date(data.start_time);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear().toString().slice(-2);

  const tableData = generateTableData();

  // Detecta la convención del servicio para no romper los históricos:
  // - Servicios viejos: el texto trae "... - grupoN" -> se renderiza el layout clásico (Grupo 1-4).
  // - Servicios nuevos: el texto trae "Seccion -- texto" -> se renderiza el layout por secciones.
  const isLegacy = tableData.some((item) => /grupo\d/.test(item.task));

  // ---- Layout clásico (servicios históricos): por Grupo 1-4 ----
  const groupedData = groupTableDataByGroup();
  const group1 = groupedData.grupo1;
  const group2 = groupedData.grupo2;
  const group3 = groupedData.grupo3;
  const group4 = groupedData.grupo4;

  function groupTableDataByGroup() {
    const groupedData = {
      grupo1: [],
      grupo2: [],
      grupo3: [],
      grupo4: [],
    };

    tableData.forEach((item) => {
      const match = item.task.match(/grupo(\d)/);
      if (match) {
        const group = `grupo${match[1]}`;
        if (groupedData[group]) {
          groupedData[group].push(item);
        }
      }
    });

    return groupedData;
  }

  // ---- Layout nuevo (servicios nuevos): por secciones de periodicidad ----
  // El texto de cada pregunta viene como "Seccion -- texto". Se separa por "--",
  // se agrupa por la seccion y se ordena por el campo "order" de la pregunta.
  const SECTIONS = [
    { key: "mensual", title: "ACTIVIDADES MENSUALES" },
    { key: "bimestral", title: "ACTIVIDAD BIMESTRAL" },
    { key: "trimestral", title: "ACTIVIDAD TRIMESTRAL" },
    { key: "semestral", title: "ACTIVIDAD SEMESTRAL" },
    { key: "anual", title: "ACTIVIDAD ANUAL (INGENIERIA)" },
  ];

  function groupTableDataBySection() {
    const grouped = {
      mensual: [],
      bimestral: [],
      trimestral: [],
      semestral: [],
      anual: [],
    };

    tableData.forEach((item) => {
      const parts = item.task.split("--");
      if (parts.length < 2) return; // observaciones u otras sin seccion: no van en las tablas
      const section = parts[0].trim().toLowerCase();
      const text = parts.slice(1).join("--").trim();
      if (grouped[section]) {
        grouped[section].push({ ...item, task: text });
      }
    });

    // Ordena cada seccion por el "order" de la pregunta para respetar la secuencia del cliente
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    });

    return grouped;
  }

  const sectionData = groupTableDataBySection();

  // Agrupa un arreglo en pares, para mostrar la seccion mensual en 2 columnas
  function pairRows(arr) {
    const rows = [];
    for (let i = 0; i < arr.length; i += 2) {
      rows.push([arr[i], arr[i + 1] || null]);
    }
    return rows;
  }

  function generateTableData() {
    const statusMap = data.schedule_checklists.reduce((map, item) => {
      map[item.checklist_question_id] = item.notes || "";
      return map;
    }, {});

    const tableData = data.questions.map((question) => {
      const status = statusMap[question.id] || "";
      return {
        task: question.question,
        order: question.order,
        status: status === "" ? "-" : "✔",
      };
    });

    return tableData;
  }

  // Estilos reutilizables para las tablas del layout nuevo
  const thStyle = {
    border: "1px solid black",
    padding: "1px",
    backgroundColor: "#f2f2f2",
    fontSize: "8px",
  };
  const tdStyle = {
    border: "1px solid black",
    padding: "1px",
    fontSize: "8px",
    lineHeight: "1.1",
  };

  // Tabla de una seccion pequeña (Bimestral, Trimestral, Semestral, Anual)
  function renderSectionTable(title, rows) {
    return (
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th colSpan={2} style={thStyle}>
              {title}
            </th>
          </tr>
          <tr>
            <th style={thStyle}>Actividad</th>
            <th style={thStyle}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td style={tdStyle}>{row.task}</td>
              <td style={tdStyle}>{row.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // Formatear horas
  const startTime = new Date(data.start_time).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const endTime = new Date(data.end_time).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  // Se define el tamaño A4 en mm convertido a px (1mm ≈ 3.78px)
  const containerStyle = {
    fontFamily: "Arial, sans-serif",
    fontSize: "10px",
    border: "1px solid black",
    width: `${210 * 3.78}px`, // 210 mm convertidos a px
    height: `${297 * 3.78}px`, // 297 mm convertidos a px
    boxSizing: "border-box",
  };

  return (
    <div ref={reporteRef} style={containerStyle}>
      {/* Header */}
      <div style={{ display: "flex", margin: "0 10px" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" style={{ fontSize: "10px", marginTop: "10px" }}>
            F-MTTO {data?.id}. Mantenimiento preventivo transporte vertical
          </Typography>
          <div style={{ marginTop: "30px" }}>
            <h1 style={{ fontSize: "16px", marginBottom: "0" }}>
              REPORTE DE VISITA MANTENIMIENTO EQUIPOS
            </h1>
            <h2 style={{ fontSize: "16px" }}>
              ASCENSORES
            </h2>
          </div>
        </div>
        <div style={{ width: "20%", marginTop: "10px" }}>
          <img src={logo} alt="Logo" />
        </div>
      </div>

      {/* Fecha */}
      <div style={{ display: "flex" }}>
        <div
          style={{
            width: "50%",
            textAlign: "center",
            borderTop: "1px solid black",
            boxSizing: "border-box",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px", margin: "15px 10px" }}>
            Fecha Visita:
          </Typography>
        </div>
        <div
          style={{
            width: "50%",
            textAlign: "center",
            borderTop: "1px solid black",
            borderLeft: "1px solid black",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "flex" }}>
            <div
              style={{
                borderRigth: "1px solid black",
                borderBottom: "1px solid black",
                boxSizing: "border-box",
                width: "33.3%",
              }}
            >
              <Typography variant="h6" style={{ fontSize: "16px", margin: "1px 10px" }}>
                Dia
              </Typography>
            </div>
            <div
              style={{
                borderRight: "1px solid black",
                borderBottom: "1px solid black",
                borderLeft: "1px solid black",
                boxSizing: "border-box",
                width: "33.3%",
              }}
            >
              <Typography variant="h6" style={{ fontSize: "16px", margin: "1px 10px" }}>
                Mes
              </Typography>
            </div>
            <div
              style={{
                borderBottom: "1px solid black",
                boxSizing: "border-box",
                width: "33.3%",
              }}
            >
              <Typography variant="h6" style={{ fontSize: "16px", margin: "1px 10px" }}>
                Año
              </Typography>
            </div>
          </div>
          <div style={{ display: "flex" }}>
            <div style={{ boxSizing: "border-box", width: "33.3%" }}>
              <Typography variant="h6" style={{ fontSize: "16px", margin: "1px 10px" }}>
                {day}
              </Typography>
            </div>
            <div
              style={{
                borderRight: "1px solid black",
                borderLeft: "1px solid black",
                boxSizing: "border-box",
                width: "33.3%",
              }}
            >
              <Typography variant="h6" style={{ fontSize: "16px", margin: "1px 10px" }}>
                {month}
              </Typography>
            </div>
            <div style={{ boxSizing: "border-box", width: "33.3%" }}>
              <Typography variant="h6" style={{ fontSize: "16px", margin: "1px 10px" }}>
                {year}
              </Typography>
            </div>
          </div>
        </div>
      </div>

      {/* Nombre Cliente */}
      <div style={{ display: "flex" }}>
        <div
          style={{
            width: "20%",
            height: "10px",
            textAlign: "center",
            borderTop: "1px solid black",
            boxSizing: "border-box",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px" }}>
            Nombre Cliente:
          </Typography>
        </div>
        <div
          style={{
            width: "80%",
            textAlign: "center",
            borderTop: "1px solid black",
            borderLeft: "1px solid black",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px" }}>
            {data.client?.name} {data.place}
          </Typography>
        </div>
      </div>

      {/* Torre y Equipo */}
      <div style={{ display: "flex" }}>
        <div
          style={{
            width: "20%",
            textAlign: "center",
            borderTop: "1px solid black",
            boxSizing: "border-box",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px" }}>
            Torre:
          </Typography>
        </div>
        <div
          style={{
            width: "20%",
            textAlign: "center",
            borderTop: "1px solid black",
            borderLeft: "1px solid black",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px" }}>
            {data.elevator?.building?.name}
          </Typography>
        </div>
        <div
          style={{
            width: "60%",
            padding: "0 2%",
            textAlign: "center",
            borderTop: "1px solid black",
            borderLeft: "1px solid black",
            boxSizing: "border-box",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px" }}>
            Equipo: {data.elevator?.identifier}
          </Typography>
          <Typography variant="h6" style={{ fontSize: "16px" }}>
            Estado: {t(data.status)}
          </Typography>
        </div>
      </div>

      {/* Hora de Ingreso */}
      <div style={{ display: "flex" }}>
        <div
          style={{
            width: "70%",
            height: "10px",
            textAlign: "center",
            borderTop: "1px solid black",
            boxSizing: "border-box",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px" }}>
            Hora Ingreso (24hrs):
          </Typography>
        </div>
        <div
          style={{
            width: "30%",
            textAlign: "center",
            borderTop: "1px solid black",
            borderLeft: "1px solid black",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px" }}>
            {startTime}
          </Typography>
        </div>
      </div>

      {/* Hora de Salida */}
      <div style={{ display: "flex" }}>
        <div
          style={{
            width: "70%",
            textAlign: "center",
            borderTop: "1px solid black",
            borderBottom: "1px solid black",
            boxSizing: "border-box",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px" }}>
            Hora Salida (24hrs):
          </Typography>
        </div>
        <div
          style={{
            width: "30%",
            textAlign: "center",
            borderTop: "1px solid black",
            borderBottom: "1px solid black",
            borderLeft: "1px solid black",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px" }}>
            {endTime}
          </Typography>
        </div>
      </div>

      {isLegacy ? (
        // ===== Layout clásico (servicios históricos): Grupo 1-4. NO se modifica. =====
        <div style={{ display: "flex" }}>
          <div style={{ width: "70%" }}>
            <div style={{ display: "flex" }}>
              <div style={{ width: "100%", textAlign: "center", padding: "10px" }}>
                {/* Tabla Grupo 1 */}
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid black", padding: "2px", backgroundColor: "#f2f2f2" }}>
                        Grupo 1
                      </th>
                      <th style={{ border: "1px solid black", padding: "2px", backgroundColor: "#f2f2f2" }}>
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {group1.map((row, index) => (
                      <tr key={index}>
                        <td style={{ border: "1px solid black", padding: "2px" }}>
                          {row.task.substr(0, row.task.length - 9)}
                        </td>
                        <td style={{ border: "1px solid black", padding: "2px" }}>
                          {row.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ width: "100%", textAlign: "center", padding: "10px" }}>
                {/* Tabla Grupo 2 */}
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid black", padding: "2px", backgroundColor: "#f2f2f2" }}>
                        Grupo 2
                      </th>
                      <th style={{ border: "1px solid black", padding: "2px", backgroundColor: "#f2f2f2" }}>
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {group2.map((row, index) => (
                      <tr key={index}>
                        <td style={{ border: "1px solid black", padding: "2px" }}>
                          {row.task.substr(0, row.task.length - 9)}
                        </td>
                        <td style={{ border: "1px solid black", padding: "2px" }}>
                          {row.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: "flex" }}>
              <div style={{ width: "100%", textAlign: "center", padding: "10px" }}>
                {/* Tabla Grupo 3 */}
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid black", padding: "2px", backgroundColor: "#f2f2f2" }}>
                        Grupo 3
                      </th>
                      <th style={{ border: "1px solid black", padding: "2px", backgroundColor: "#f2f2f2" }}>
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {group3.map((row, index) => (
                      <tr key={index}>
                        <td style={{ border: "1px solid black", padding: "2px" }}>
                          {row.task.substr(0, row.task.length - 9)}
                        </td>
                        <td style={{ border: "1px solid black", padding: "2px" }}>
                          {row.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ width: "100%", textAlign: "center", padding: "10px" }}>
                {/* Tabla Grupo 4 */}
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid black", padding: "2px", backgroundColor: "#f2f2f2" }}>
                        Grupo 4
                      </th>
                      <th style={{ border: "1px solid black", padding: "2px", backgroundColor: "#f2f2f2" }}>
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {group4.map((row, index) => (
                      <tr key={index}>
                        <td style={{ border: "1px solid black", padding: "2px" }}>
                          {row.task.substr(0, row.task.length - 9)}
                        </td>
                        <td style={{ border: "1px solid black", padding: "2px" }}>
                          {row.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div style={{ width: "30%", marginTop: "20px" }}>
            <img src={image} alt="Imagen" />
          </div>
        </div>
      ) : (
        // ===== Layout nuevo (servicios nuevos): secciones por periodicidad =====
        <div>
          {/* Seccion Mensual a todo lo ancho, en 2 columnas */}
          <div style={{ padding: "3px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th colSpan={4} style={thStyle}>
                    {SECTIONS[0].title}
                  </th>
                </tr>
                <tr>
                  <th style={thStyle}>Actividad</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Actividad</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pairRows(sectionData.mensual).map((pair, index) => (
                  <tr key={index}>
                    <td style={tdStyle}>{pair[0] ? pair[0].task : ""}</td>
                    <td style={tdStyle}>{pair[0] ? pair[0].status : ""}</td>
                    <td style={tdStyle}>{pair[1] ? pair[1].task : ""}</td>
                    <td style={tdStyle}>{pair[1] ? pair[1].status : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Secciones pequeñas (2x2) + diagrama del ascensor */}
          <div style={{ display: "flex" }}>
            <div style={{ width: "70%" }}>
              <div style={{ display: "flex" }}>
                <div style={{ width: "50%", textAlign: "center", padding: "3px" }}>
                  {renderSectionTable(SECTIONS[1].title, sectionData.bimestral)}
                </div>
                <div style={{ width: "50%", textAlign: "center", padding: "3px" }}>
                  {renderSectionTable(SECTIONS[2].title, sectionData.trimestral)}
                </div>
              </div>
              <div style={{ display: "flex" }}>
                <div style={{ width: "50%", textAlign: "center", padding: "3px" }}>
                  {renderSectionTable(SECTIONS[3].title, sectionData.semestral)}
                </div>
                <div style={{ width: "50%", textAlign: "center", padding: "3px" }}>
                  {renderSectionTable(SECTIONS[4].title, sectionData.anual)}
                </div>
              </div>
            </div>
            <div style={{ width: "30%", marginTop: "4px", textAlign: "center" }}>
              <img
                src={image}
                alt="Imagen"
                style={{ maxWidth: "100%", maxHeight: "210px", objectFit: "contain" }}
              />
            </div>
          </div>
        </div>
      )}
      <div style={{ display: "flex" }}>
        <div style={{ width: "100%", textAlign: "center", borderTop: "1px solid black", boxSizing: "border-box" }}>
          <Typography variant="h6" style={{ fontSize: "10px" }}>
            Observaciones
          </Typography>
        </div>
      </div>
      <div style={{ display: "flex" }}>
        <div style={{ width: "100%", textAlign: "center", borderTop: "1px solid black", boxSizing: "border-box" }}>
          <Typography variant="h6" style={{ fontSize: "10px" }}>
            {data.description}
          </Typography>
        </div>
      </div>
      <div style={{ display: "flex", marginTop: "0" }}>
        <div
          style={{
            width: "50%",
            textAlign: "center",
            borderTop: "1px solid black",
            boxSizing: "border-box",
            padding: "10px",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px", margin: "5px 0" }}>
            Firma Cliente
          </Typography>
          <div style={{ height: "100px" }}>
            {data.signature_url && (
              <img
                src={data.signature_url}
                alt="Firma Cliente"
                style={{ maxHeight: "100%", maxWidth: "100%" }}
                crossOrigin="anonymous"
              />
            )}
          </div>
          <Typography variant="h6" style={{ fontSize: "14px", margin: "5px 0" }}>
            Nombre: {data.person_name}
          </Typography>
          <Typography variant="h6" style={{ fontSize: "14px", margin: "5px 0" }}>
            C.C: {data.document}
          </Typography>
        </div>
        <div
          style={{
            width: "50%",
            textAlign: "center",
            borderTop: "1px solid black",
            boxSizing: "border-box",
            padding: "10px",
          }}
        >
          <Typography variant="h6" style={{ fontSize: "16px", margin: "5px 0" }}>
            Firma Técnico
          </Typography>
          <div style={{ height: "100px" }}>
            {data.technicianSignature && (
              <img
                src={data.technicianSignature}
                alt="Firma Técnico"
                style={{ maxHeight: "100%", maxWidth: "100%" }}
              />
            )}
          </div>
          <Typography variant="h6" style={{ fontSize: "14px", margin: "5px 0" }}>
            Nombre: {data.user?.name}
          </Typography>
          <Typography variant="h6" style={{ fontSize: "14px", margin: "5px 0" }}>
            C.C: {data.user?.document}
          </Typography>
        </div>
      </div>
    </div>
  );
};
